import { DndContext, closestCenter, type DragEndEvent } from '@dnd-kit/core'
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable'
import { useMemo } from 'react'
import type { ReactNode } from 'react'

import { cn } from '@/shared/lib/cn'
import { neighborsForMove } from '@/shared/lib/rank'

import { SortableItem } from './SortableItem'
import { createAnnouncements, createInstructions } from './announcements'
import { useDndSensors } from './sensors'
import type { ReorderPayload, SortableEntity, SortableRenderState } from './types'
import { useT } from '@/shared/i18n'

export interface SortableListProps<T extends SortableEntity> {
  items: readonly T[]
  /** Se llama UNA vez por drop efectivo, con la posición ya calculada. */
  onReorder: (payload: ReorderPayload) => void
  renderItem: (item: T, state: SortableRenderState) => ReactNode
  /** Nombre humano del item, para el `aria-label` del handle. */
  getLabel: (item: T) => string
  ariaLabel: string
  /** Un item que devuelve `true` no se arrastra (los ongoing, por ejemplo). */
  isDisabled?: (item: T) => boolean
  disabledReason?: string
  /** Se soltó sobre una zona que rechaza el drop. */
  onRejectedDrop?: (zoneId: string) => void
  /**
   * Se dibuja DENTRO del `DndContext`, después de la lista: ahí va la sección
   * de permanentes, que necesita estar en el contexto para poder rechazar.
   */
  children?: ReactNode
  className?: string
}

/**
 * Lista ordenable de una sola columna.
 *
 * Del drop deriva los VECINOS del item (`neighborsForMove`) y los entrega en el
 * payload. El rank nuevo lo calcula el server: acá no se genera ninguna clave
 * (el contrato está en `shared/hooks/use-reorder-mutation.ts`).
 */
export function SortableList<T extends SortableEntity>({
  items,
  onReorder,
  renderItem,
  getLabel,
  ariaLabel,
  isDisabled,
  disabledReason,
  onRejectedDrop,
  children,
  className,
}: SortableListProps<T>) {
  const t = useT()
  const sensors = useDndSensors()
  const ids = useMemo(() => items.map((item) => item.id), [items])
  const announcements = useMemo(() => createAnnouncements(t, 'list'), [t])
  const instructions = useMemo(() => createInstructions(t), [t])

  function handleDragEnd({ active, over }: DragEndEvent) {
    if (!over) return

    const fromIndex = items.findIndex((item) => item.id === active.id)
    const newIndex = items.findIndex((item) => item.id === over.id)

    if (newIndex === -1) {
      // No cayó sobre ningún item de la lista: es una zona que rechaza.
      if (over.id !== active.id) onRejectedDrop?.(String(over.id))
      return
    }
    if (fromIndex === -1 || fromIndex === newIndex) return

    const { beforeId, afterId } = neighborsForMove(items, fromIndex, newIndex)

    onReorder({ itemId: String(active.id), fromIndex, newIndex, beforeId, afterId })
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      accessibility={{ announcements, screenReaderInstructions: instructions }}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={ids} strategy={verticalListSortingStrategy}>
        <div role="list" aria-label={ariaLabel} className={cn('flex flex-col', className)}>
          {items.map((item, index) => {
            const disabled = isDisabled?.(item) ?? false
            return (
              <SortableItem
                key={item.id}
                id={item.id}
                index={index}
                label={getLabel(item)}
                disabled={disabled}
                disabledReason={disabledReason}
              >
                {(state) => renderItem(item, state)}
              </SortableItem>
            )
          })}
        </div>
      </SortableContext>

      {children}
    </DndContext>
  )
}
